import { z } from 'zod';

// Parse string values to boolean (e.g., "true", "1", true, 1)
const zCoerceBoolean = z.preprocess(
  (val) => val === 'true' || val === '1' || val === true || val === 1,
  z.boolean()
);

/** Whether API requests should be proxied through Vite, e.g. for local development */
const isApiProxied = zCoerceBoolean
  .default(false)
  .parse(import.meta.env.VITE_PROXY_API);

const envSchema = z.object({
  /** Whether the application is running in development mode */
  dev: zCoerceBoolean.default(false),
  /**
   * The current Vite mode
   * @see https://vite.dev/guide/env-and-mode#modes
   */
  mode: z.string(),

  /** The current Evervault environment */
  environment: z.enum(['local', 'staging', 'production']).default('local'),

  /** Whether to proxy API requests through Vite */
  apiUrls: z.object({
    /** The public API (CAPI) */
    public: z.string().default('https://api.evervault.com'),
    /** The internal API (Node, routed via Relay) */
    internal: z
      .string()
      .default('https://internal-api-evervault-com.relay.evervault.com')
      .transform((val) => (isApiProxied ? '/api' : val)),
    /** The internal API URL (Node, direct) */
    internalDirect: z
      .string()
      .default('https://internal-api.evervault.com')
      .transform((val) => (isApiProxied ? '/direct-api' : val)),
    /** The keys API URL */
    keys: z.string().optional(),
    /**
     * The Evervault UI components host, which serves the Card iframe. Each
     * environment has its own, since the iframe encrypts with its keys.
     */
    components: z.string().default('https://ui-components.evervault.com'),
  }),

  // First-party configuration
  applePay: z.object({
    identifierFile: z.string().optional(),
  }),
  pageProtection: z.object({
    scriptUrl: z.string().optional(),
  }),

  // Third-party integrations
  auth0: z.object({
    domain: z.string(),
    clientId: z.string(),
    audience: z.string().optional(),
  }),
  github: z.object({
    appName: z.string(),
  }),
  launchDarkly: z.object({
    clientId: z.string(),
  }),
  plain: z.object({
    appId: z.string().optional(),
  }),
  slack: z.object({
    clientId: z.string().optional(),
  }),
  vanta: z.object({
    clientId: z.string(),
  }),

  // Monitoring & analytics
  customerio: z.object({
    writeKey: z.string().optional(),
  }),
  datadog: z.object({
    debugMode: zCoerceBoolean.default(false),
    applicationId: z.string().optional(),
    clientToken: z.string().optional(),
    site: z.string().default('us5.datadoghq.com'),
    version: z.string().optional(),
    env: z.string().default('development'),
  }),
});

const config: z.infer<typeof envSchema> = {
  dev: import.meta.env.DEV,
  mode: import.meta.env.MODE,

  environment: import.meta.env.VITE_EV_CONTEXT,

  apiUrls: {
    public: import.meta.env.VITE_EV_PUBLIC_API_URL,
    internal: import.meta.env.VITE_EV_API_URL,
    internalDirect: import.meta.env.VITE_EV_DIRECT_API_URL,
    keys: import.meta.env.VITE_EV_KEYS_URL,
    components: import.meta.env.VITE_EV_COMPONENTS_URL,
  },

  applePay: {
    identifierFile: import.meta.env.VITE_APPLE_PAY_IDENTIFIER_FILE,
  },
  pageProtection: {
    scriptUrl: import.meta.env.VITE_EV_PAGE_PROTECTION_SCRIPT,
  },

  auth0: {
    domain: import.meta.env.VITE_AUTH0_DOMAIN,
    clientId: import.meta.env.VITE_AUTH0_CLIENT_ID,
    audience: import.meta.env.VITE_AUTH0_AUDIENCE,
  },
  github: {
    appName: import.meta.env.VITE_GITHUB_APP,
  },
  launchDarkly: {
    clientId: import.meta.env.VITE_LAUNCH_DARKLY_CLIENT_ID,
  },
  plain: {
    appId: import.meta.env.VITE_PLAIN_APP_ID,
  },
  slack: {
    clientId: import.meta.env.VITE_SLACK_CLIENT_ID,
  },
  vanta: {
    clientId: import.meta.env.VITE_VANTA_CLIENT_ID,
  },

  customerio: {
    writeKey: import.meta.env.VITE_CUSTOMER_IO_WRITE_KEY,
  },
  datadog: {
    debugMode: import.meta.env.VITE_DATADOG_DEBUG,
    applicationId: import.meta.env.VITE_DATADOG_APPLICATION_ID,
    clientToken: import.meta.env.VITE_DATADOG_CLIENT_TOKEN,
    site: import.meta.env.VITE_DATADOG_SITE,
    version: import.meta.env.VITE_VERCEL_GIT_COMMIT_SHA,
    env: import.meta.env.VITE_VERCEL_TARGET_ENV,
  },
};

export const ENV = envSchema.parse(config);
